/* This example requires Tailwind CSS v2.0+ */
import React, { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import Button from '../General/Button';
import Alert from './Alert';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { useCommonSelector } from '@wajeez-web/stores/common';

interface MobileModalProps {
  onClose: () => void;
  title?: string | null | undefined;
  open: boolean;
  children?: JSX.Element;
  position?: string;
  onSubmit?: () => void;
  withScroll?: boolean;
  height?: 'full' | 'auto' | 'screen' | string;
  width?: 'full' | 'auto' | 'screen' | string;
  loading?: boolean;
  isCloseIcon?: boolean;
  wrapperTopPadding?: string;
  wrapperXPadding?: string;
  wrapperBgStyle?: string;
  borderRadius?: string;
  zIndex?: number;
  headerStyle?: string;
  bodyStyle?: string;
  widthStyle?: string;
  mainBodyStyle?: string;
}

const MobileModal = ({
  open,
  onClose,
  title,
  children,
  onSubmit,
  withScroll,
  loading,
  width,
  height,
  isCloseIcon = true,
  wrapperTopPadding = '0px',
  wrapperXPadding = '0px',
  wrapperBgStyle = 'bg-gray-500/75',
  borderRadius = '0px',
  zIndex = 15,
  headerStyle = 'px-4 pt-5 pb-0 sm:p-6 sm:pb-4 mb-4',
  bodyStyle = 'pt-0 pb-4 sm:p-6 sm:pt-0 sm:pb-4',
  widthStyle = 'w-1/2',
  mainBodyStyle,
}: MobileModalProps) => {
  const alert = useCommonSelector((state) => state.common.alert);
  const { dir } = useCommonSelector((state) => state.common.settings);
  // const dispatch = useCommonDispatch();
  const cancelButtonRef = useRef<any>();

  // const escFunction = useCallback((event: KeyboardEvent) => {
  //   if (event.key === 'Escape') {
  //     onClose();
  //   }
  // }, []);

  // useEffect(() => {
  //   document.addEventListener('keydown', escFunction, false);

  //   return () => {
  //     document.removeEventListener('keydown', escFunction, false);
  //     dispatch(CommonActions.setAlert({ ...alert, for: 'page' }));
  //   };
  // }, []);

  // useEffect(() => {
  //   open &&
  //     alert.for !== 'modal' &&
  //     dispatch(CommonActions.setAlert({ ...alert, for: 'modal' }));
  // }, [open]);
  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        ref={cancelButtonRef}
        as="div"
        className="fixed  m-auto inset-0"
        style={{ zIndex }}
        onClose={() => {
          onClose();
        }}
      >
        <div
          dir={dir}
          className={`flex items-end justify-center min-h-modal-screen pb-20 text-center sm:block sm:p-0`}
          style={{
            padding: `${wrapperTopPadding} ${wrapperXPadding} 0 ${wrapperXPadding}`,
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay
              className={`fixed inset-0 ${wrapperBgStyle} transition-opacity`}
            />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className={`relative inline-block max-h-screen ${
                mainBodyStyle ||
                `align-bottom bg-white dark:bg-black dark:text-white ${
                  width ? 'w-' + width : 'max-w-2xl ' + widthStyle
                }  ${
                  height ? 'h-' + height : 'h-auto'
                } text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full`
              }`}
              style={{ perspective: '10px', borderRadius }}
            >
              <div
                className={`w-full bg-white dark:bg-black ${headerStyle}`}
                style={{ borderRadius }}
              >
                {title ||
                  isCloseIcon ||
                  (alert.show && (
                    <div className="flex w-full justify-between items-center">
                      {/* {alert.for === 'modal' && (
                        <div className="mb-4">
                          <Alert />
                        </div>
                      )} */}
                      {title && (
                        <div className="flex items-center first:ms-0 flex-1 min-w-0">
                          <div className="w-full text-center leading-normal text-base font-bold">
                            <ReturnTranslatedText
                              origintext={title}
                              text={`ModalTitles.${title}`}
                            />
                          </div>
                        </div>
                      )}
                      {
                        <div
                          className={`flex items-center first:ms-0 ${
                            !isCloseIcon && 'hidden'
                          }`}
                        >
                          <div className="flex w-full justify-center items-center">
                            <div className="flex items-center justify-end first:ms-0">
                              <button
                                ref={cancelButtonRef}
                                onClick={() => onClose()}
                                type="button"
                                className="leading-normal inline-flex items-center font-normal outline-none h-8 text-md bg-transparent text-gray-800 hover:text-gray-700"
                                aria-label="Close"
                              >
                                <XIcon className="h-5 w-5 hover:text-red-500 text-warmGray-500" />
                              </button>
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  ))}
              </div>

              <div
                className={`text-start ${
                  onSubmit && 'mb-4'
                } relative ${bodyStyle} max-h-max-modal-screen ${
                  withScroll && 'scrollbar-thin scrollbar-thumb-gray-400 '
                }`}
                style={{ zIndex }}
              >
                {children}
              </div>
              {onSubmit && (
                <div className={`px-4 mt-2 pb-4 ltr:right-0 rtl:left-0`}>
                  {
                    <div className="flex flex-row justify-end">
                      <Button
                        loading={loading}
                        title="Apply changes"
                        bg="primary"
                        color="white"
                        onClick={onSubmit}
                      />
                    </div>
                  }
                </div>
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MobileModal;
